
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, TrendingUp, FileText, AlertTriangle, CheckCircle, Eye } from 'lucide-react';
import { RecentActivitiesModal } from './RecentActivitiesModal';

export const RealTimeActivities = () => {
  const [showAllActivities, setShowAllActivities] = useState(false);

  const activities = [
    {
      id: '1',
      type: 'data_entry',
      title: 'Energy consumption data updated',
      description: 'Monthly electricity usage recorded',
      timestamp: '2 hours ago',
      status: 'completed',
      color: 'bg-green-100 text-green-800 border-green-200'
    },
    {
      id: '2',
      type: 'assessment',
      title: 'ESG assessment completed',
      description: 'Q4 2024 assessment finalized',
      timestamp: '4 hours ago',
      status: 'completed',
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    {
      id: '3',
      type: 'report',
      title: 'Sustainability report generated',
      description: 'Annual report created',
      timestamp: '6 hours ago',
      status: 'pending',
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    },
    {
      id: '4',
      type: 'validation',
      title: 'Data validation required',
      description: 'Carbon calculations need review',
      timestamp: '8 hours ago',
      status: 'warning',
      color: 'bg-orange-100 text-orange-800 border-orange-200'
    }
  ];

  const getActivityIcon = (type: string, status: string) => {
    if (status === 'completed') return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (status === 'warning') return <AlertTriangle className="h-4 w-4 text-orange-500" />;
    if (status === 'pending') return <Clock className="h-4 w-4 text-blue-500" />;
    
    switch (type) {
      case 'data_entry':
        return <TrendingUp className="h-4 w-4 text-blue-500" />;
      case 'report':
        return <FileText className="h-4 w-4 text-purple-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <Card className="h-[600px] flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Activities
            </CardTitle>
            <CardDescription>Latest system activities and updates</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAllActivities(true)}
            className="flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            View All Activities
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        <div className="space-y-4">
          {activities.map((activity) => (
            <div key={activity.id} className="flex items-start gap-3 p-3 rounded-lg border hover:shadow-sm transition-shadow">
              <div className="flex-shrink-0 mt-0.5">
                {getActivityIcon(activity.type, activity.status)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-medium truncate">{activity.title}</h4>
                  <Badge className={`text-xs ${activity.color}`}>
                    {activity.status}
                  </Badge>
                </div>
                <p className="text-xs text-gray-600 mb-2">{activity.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{activity.timestamp}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <RecentActivitiesModal
        isOpen={showAllActivities}
        onClose={() => setShowAllActivities(false)}
      />
    </Card>
  );
};
